"use client";

import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import Link from "@/components/Link";
import { usePathname, useSearchParams } from "next/navigation";
import { useRouter } from "@/lib/utm/hooks";
import PackageCard from "@/app/(main)/components/PackageCard";
import EmptyState from "@/app/(main)/components/EmptyState";
import Pagination from "@/app/(main)/components/Pagination";
import DestinationFilterWidget from "@/app/(main)/components/DestinationFilterWidget";
import useScrollToListTop from "@/app/(main)/components/useScrollToListTop";
import { AppRoutes } from "@/lib/routeResolver";
import type { HomePackage, SystemCountry } from "@/lib/navigation";
import {
  fetchTravelIdeasClient,
  TRAVEL_IDEAS_PAGE_SIZE,
} from "@/lib/travelIdeas";

interface TravelIdeasClientProps {
  initialPackages: HomePackage[];
  initialTotalPages: number;
  initialCount: number;
  countries: SystemCountry[];
  /** Country id resolved from the URL's ?country param (empty = none). */
  initialSelectedCountryId: string;
  /** Total ignoring any country filter — drives whether the filter shows. */
  unfilteredCount: number;
  pageSize?: number;
  /** Server-rendered sidebar extras. Server Components can't be imported by a
   *  client component, so they arrive as an element. */
  sidebarExtra?: ReactNode;
}

export default function TravelIdeasClient({
  initialPackages,
  initialTotalPages,
  initialCount,
  countries,
  initialSelectedCountryId,
  unfilteredCount,
  pageSize = TRAVEL_IDEAS_PAGE_SIZE,
  sidebarExtra,
}: TravelIdeasClientProps) {
  const [packages, setPackages] = useState(initialPackages);
  const [totalPages, setTotalPages] = useState(initialTotalPages);
  const [totalCount, setTotalCount] = useState(initialCount);
  const [selectedCountry, setSelectedCountry] = useState(
    initialSelectedCountryId,
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isFirstRender = useRef(true);
  const scrollToListTop = useScrollToListTop(containerRef);

  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const loadPackages = useCallback(
    async (page: number, countryId: string) => {
      setLoading(true);
      try {
        const result = await fetchTravelIdeasClient(
          page,
          pageSize,
          countryId || undefined,
        );
        setPackages(result.packages);
        setTotalPages(result.totalPages);
        setTotalCount(result.count);
      } finally {
        setLoading(false);
      }
    },
    [pageSize],
  );

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    loadPackages(currentPage, selectedCountry);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, selectedCountry]);

  const selectedName = countries.find((c) => c.id === selectedCountry)?.name;

  // Carry the country *name* in the URL, to match how the rest of the site
  // links into filtered listings.
  function buildHref(countryId: string) {
    const params = new URLSearchParams(searchParams.toString());
    const name = countries.find((c) => c.id === countryId)?.name;
    if (name) params.set("country", name);
    else params.delete("country");
    const qs = params.toString();
    return qs ? `${pathname}?${qs}` : pathname;
  }

  function handleCountryChange(countryId: string) {
    setSelectedCountry(countryId);
    setCurrentPage(1);
    router.replace(buildHref(countryId), { scroll: false });
  }

  function handlePageChange(page: number) {
    setCurrentPage(page);
    containerRef.current?.focus({ preventScroll: true });
    scrollToListTop();
  }

  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  return (
    <div ref={containerRef} tabIndex={-1}>
      <div className="row">
        <div className="col-xxl-8 col-lg-7">
          {totalCount > 0 && (
            <p className="blog-text mb-4">
              Showing{" "}
              <strong>
                {startItem}–{endItem}
              </strong>{" "}
              of <strong>{totalCount}</strong> travel ideas
              {selectedName ? ` in ${selectedName}` : ""}
            </p>
          )}

          <div
            className="row g-4"
            style={{ opacity: loading ? 0.5 : 1, transition: "opacity 0.2s" }}
          >
            {packages.map((pack) => (
              <div key={pack.id} className="col-sm-6">
                <PackageCard pack={pack} isTravelIdea />
              </div>
            ))}

            {packages.length === 0 && !loading && (
              <div className="col-12">
                {selectedName ? (
                  <EmptyState
                    icon="fa-regular fa-filter-circle-xmark"
                    title="No travel ideas here yet"
                    text={`We don't have a travel idea for ${selectedName} at the moment. Clear the filter to see every idea, or tell us what you have in mind and we'll build one.`}
                    action={
                      <Link
                        href={buildHref("")}
                        className="th-btn style1 th-icon"
                        title="Show all travel ideas"
                        onClick={(e) => {
                          e.preventDefault();
                          handleCountryChange("");
                        }}
                      >
                        Show All Travel Ideas
                      </Link>
                    }
                  />
                ) : (
                  <EmptyState
                    icon="fa-regular fa-lightbulb"
                    title="No travel ideas published yet"
                    text="We're curating this collection now. In the meantime, browse our destinations or tell us where you'd like to go."
                    action={
                      <Link
                        href={AppRoutes.requestQuote()}
                        className="th-btn style1 th-icon"
                        title="Request a custom quote"
                      >
                        Plan My Trip
                      </Link>
                    }
                  />
                )}
              </div>
            )}
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            disabled={loading}
            label="Travel ideas pagination"
          />
        </div>

        <div className="col-xxl-4 col-lg-5">
          <aside className="sidebar-area position-sticky" style={{ top: 100 }}>
            {/* Unfiltered count, not the current page's: hide the filter only
                when nothing is published at all, never when a country simply
                has no matches — that would strand the user on their own
                selection. */}
            {unfilteredCount > 0 && (
              <DestinationFilterWidget
                title="Filter By Destination"
                options={countries.map((c) => ({ id: c.id, label: c.name }))}
                selectedId={selectedCountry}
                onSelect={handleCountryChange}
                hrefFor={(option) => buildHref(option.id)}
              />
            )}
            {sidebarExtra}
          </aside>
        </div>
      </div>
    </div>
  );
}
